import React, { useState } from 'react';
import { AlertTriangle, Search, Filter, Clock, User, CheckCircle, XCircle, Download, Bell } from 'lucide-react';
import './Alerts.css';

const Alerts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock alerts data
  const alerts = [
    {
      id: 1,
      patientId: 42,
      patientName: 'Patient 42',
      severity: 'high',
      type: 'missed_dose',
      title: 'Multiple Missed Doses',
      description: 'Patient has missed 3 consecutive doses of Metformin',
      timestamp: '2 hours ago',
      date: 'Aug 28, 2025, 6:45 PM',
      status: 'active',
      adherenceImpact: '15%'
    },
    {
      id: 2,
      patientId: 44,
      patientName: 'Patient 44',
      severity: 'critical',
      type: 'device_offline',
      title: 'Device Offline',
      description: 'HiDo device has been offline for 12 hours',
      timestamp: '12 hours ago',
      date: 'Aug 28, 2025, 8:45 AM',
      status: 'active',
      adherenceImpact: '0%'
    },
    {
      id: 3,
      patientId: 48,
      patientName: 'Patient 48',
      severity: 'high',
      type: 'low_adherence',
      title: 'Low Adherence Rate',
      description: 'Patient adherence has dropped below 40% this week',
      timestamp: '4 hours ago',
      date: 'Aug 28, 2025, 4:45 PM',
      status: 'active',
      adherenceImpact: '38%'
    },
    {
      id: 4,
      patientId: 52,
      patientName: 'Patient 52',
      severity: 'medium',
      type: 'late_dose',
      title: 'Consistently Late Doses',
      description: 'Patient has taken medications 2+ hours late for the past 5 days',
      timestamp: '1 day ago',
      date: 'Aug 27, 2025, 8:45 PM',
      status: 'acknowledged',
      adherenceImpact: '8%'
    },
    {
      id: 5,
      patientId: 57,
      patientName: 'Patient 57',
      severity: 'low',
      type: 'device_warning',
      title: 'Low Battery Warning',
      description: 'Device battery level below 20%',
      timestamp: '3 hours ago',
      date: 'Aug 28, 2025, 5:45 PM',
      status: 'active',
      adherenceImpact: '0%'
    },
    {
      id: 6,
      patientId: 63,
      patientName: 'Patient 63',
      severity: 'medium',
      type: 'behavior',
      title: 'Unusual Medication Pattern',
      description: 'Patient dispensed medication but video shows possible non-consumption',
      timestamp: '8 hours ago',
      date: 'Aug 28, 2025, 12:45 PM',
      status: 'resolved',
      adherenceImpact: '5%'
    },
    {
      id: 7,
      patientId: 45,
      patientName: 'Patient 45',
      severity: 'low',
      type: 'schedule_change',
      title: 'Schedule Modification Requested',
      description: 'Patient requested to change evening dose time from 6 PM to 8 PM',
      timestamp: '1 day ago',
      date: 'Aug 27, 2025, 3:00 PM',
      status: 'resolved',
      adherenceImpact: '0%'
    },
    {
      id: 8,
      patientId: 50,
      patientName: 'Patient 50',
      severity: 'medium',
      type: 'missed_dose',
      title: 'Missed Evening Dose',
      description: 'Patient missed evening medication dose',
      timestamp: '2 days ago',
      date: 'Aug 26, 2025, 8:00 PM',
      status: 'acknowledged',
      adherenceImpact: '10%'
    }
  ];

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = 
      alert.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeverity = 
      severityFilter === 'all' || alert.severity === severityFilter;
    
    const matchesStatus = 
      statusFilter === 'all' || alert.status === statusFilter;
    
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#f59e0b';
      case 'low': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { text: 'Active', class: 'status-active', icon: Bell },
      acknowledged: { text: 'Acknowledged', class: 'status-acknowledged', icon: CheckCircle },
      resolved: { text: 'Resolved', class: 'status-resolved', icon: CheckCircle }
    };
    return statusConfig[status] || statusConfig.active;
  };

  const getAlertStats = () => {
    return {
      total: alerts.length,
      active: alerts.filter(a => a.status === 'active').length,
      critical: alerts.filter(a => a.severity === 'critical').length,
      high: alerts.filter(a => a.severity === 'high').length
    };
  };

  const stats = getAlertStats();

  return (
    <div className="alerts-page">
      <div className="alerts-header">
        <div className="header-content">
          <h1>Alerts & Notifications</h1>
          <p>Monitor and manage patient alerts and system notifications</p>
        </div>
        <div className="header-actions">
          <button className="action-button secondary">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="alerts-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f1f5f9', color: '#3b82f6' }}>
            <AlertTriangle size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Alerts</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
            <Bell size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
            <XCircle size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.critical}</div>
            <div className="stat-label">Critical</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fff7ed', color: '#ea580c' }}>
            <AlertTriangle size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.high}</div>
            <div className="stat-label">High Priority</div>
          </div>
        </div>
      </div>

      <div className="alerts-filters">
        <div className="search-filter">
          <Search className="search-icon" size={16} />
          <input
            type="text"
            placeholder="Search alerts by patient, title, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-search"
          />
        </div>
        
        <div className="filter-dropdowns">
          <button className="more-filters">
            <Filter size={16} />
            Filters
          </button>
          <select 
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="filter-dropdown"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-dropdown"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div className="alerts-list">
        {filteredAlerts.map((alert) => {
          const statusBadge = getStatusBadge(alert.status);
          const StatusIcon = statusBadge.icon;
          
          return (
            <div key={alert.id} className={`alert-card ${alert.severity}`}>
              <div className="alert-severity">
                <div 
                  className="severity-indicator"
                  style={{ backgroundColor: getSeverityColor(alert.severity) }}
                ></div>
              </div>
              <div className="alert-content">
                <div className="alert-header">
                  <div className="alert-title-section">
                    <AlertTriangle 
                      size={20} 
                      style={{ color: getSeverityColor(alert.severity) }}
                    />
                    <h3>{alert.title}</h3>
                  </div>
                  <div className={`alert-status-badge ${statusBadge.class}`}>
                    <StatusIcon size={14} />
                    {statusBadge.text}
                  </div>
                </div>
                <p className="alert-description">{alert.description}</p>
                <div className="alert-meta">
                  <div className="meta-item">
                    <User size={14} />
                    <span>{alert.patientName}</span>
                  </div>
                  <div className="meta-item">
                    <Clock size={14} />
                    <span>{alert.timestamp}</span>
                  </div>
                  {parseInt(alert.adherenceImpact) > 0 && (
                    <div className="meta-item impact">
                      <AlertTriangle size={14} />
                      <span>Impact: -{alert.adherenceImpact}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="alert-actions">
                {alert.status === 'active' && (
                  <>
                    <button className="action-btn-small primary">Acknowledge</button>
                    <button className="action-btn-small">Resolve</button>
                  </>
                )}
                {alert.status === 'acknowledged' && (
                  <button className="action-btn-small">Resolve</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredAlerts.length === 0 && (
        <div className="no-results">
          <AlertTriangle size={48} />
          <h3>No alerts found</h3>
          <p>Try adjusting your filters or search terms</p>
        </div>
      )}
    </div>
  );
};

export default Alerts;

